/**
 * Service layer — Subconsultant RFP -> Proposal -> Client Approval (Supabase-backed)
 *
 * Wraps /api/consultant-rfps (Clerk-authed, service_role). Sits alongside the existing
 * Candidate/Awarded lifecycle on Airtable `Project Consultants`
 * (services/projectConsultants.js) — joined by projectConsultantId (soft ref, no schema
 * change on the Airtable side). Components call only these functions. See docs/TEAM.md.
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/consultant-rfps' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'Consultant RFP request failed: ' + res.status)
  }
  return res.json()
}

export const CLIENT_DECISION_LABELS = {
  approved: 'Approved',
  revision_requested: 'Revision requested',
  declined: 'Declined',
}

/* ── Reads ────────────────────────────────────────────────────────────────── */

/**
 * Every RFP (with recipients) and proposal for a project's candidates, in one fetch.
 * @param {string} projectId
 * @param {string[]} [projectConsultantIds] — every Project Consultants row id for this project
 *   (from services/projectConsultants.js#getProjectConsultants); scopes the proposals returned so
 *   ones logged with no RFP round on file (rfp_id null) still show up.
 */
export function getProjectConsultantRfpData(projectId, projectConsultantIds) {
  if (!projectId) return Promise.resolve({ rfps: [], proposals: [] })
  let qs = 'projectId=' + encodeURIComponent(projectId)
  if (projectConsultantIds && projectConsultantIds.length) qs += '&pcIds=' + encodeURIComponent(projectConsultantIds.join(','))
  return call('GET', null, qs).then(d => ({ rfps: d.rfps || [], proposals: d.proposals || [], replies: d.replies || [], hireBy: d.hireBy || {} }))
}

/**
 * Everything the send window needs before an RFP goes out: the project's saved facts, a suggested
 * work type, the scope of work (the title sheet's wording), the standard services for this
 * consultant, and the project header. { facts, suggestedWorkType, scope:{groups}, services, project }
 */
export function getRfpPrep(projectId, service) {
  return call('GET', null, 'action=prep&projectId=' + encodeURIComponent(projectId) + '&service=' + encodeURIComponent(service || ''))
}

/* ── Writes ───────────────────────────────────────────────────────────────── */

/* Proposals that file themselves (docs/TEAM.md, 2026-11-02): the RFP sender's review, the
   signed-copy reminder, and who hires the candidate. Each returns { done } (the words for the row). */

/** decision: 'send' (to the client's Hub) | 'accept' (we hire: award) | 'revision' (message required) | 'decline'. */
export function reviewConsultantProposal({ proposalId, decision, note, message, userName, userEmail }) {
  return call('POST', { action: 'reviewProposal', proposalId, decision, note, message, userName, userEmail })
}
/** answer: 'later' (back in 2 working days) | 'direct' (the owner signed with them directly). */
export function answerSignedReminder({ proposalId, answer, userName }) {
  return call('POST', { action: 'signedReminder', proposalId, answer, userName })
}
/** The team attaches the signed copy the owner sent us (a PDF or a photo of the signed page): saved,
 *  emailed to the consultant in the owner's name, and the candidate awarded. Returns { done }. */
export async function attachSignedCopy(proposalId, file, userName) {
  const { signedUrl, path } = await call('POST', { action: 'signedUploadUrl', proposalId, filename: file.name })
  const put = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file })
  if (!put.ok) throw new Error('The upload did not go through. Please try again.')
  return call('POST', { action: 'attachSigned', proposalId, path, filename: file.name, userName })
}
/** A signed copy may be a PDF or a photo of the signed page. */
export const SIGNED_COPY_ACCEPT = 'application/pdf,.pdf,image/jpeg,image/png,image/heic,image/heif,image/webp,.jpg,.jpeg,.png,.heic,.heif,.webp'
export function signedCopyOk(file) {
  return !!file && (/^(application\/pdf|image\/(jpeg|png|heic|heif|webp))$/i.test(file.type || '') || /\.(pdf|jpe?g|png|heic|heif|webp)$/i.test(file.name || ''))
}

/** hireBy: 'owner' (goes to the client's Hub) | 'firm' (we accept and award). */
export function setConsultantHireBy(projectConsultantId, hireBy, userName) {
  return call('POST', { action: 'setHireBy', projectConsultantId, hireBy, userName })
}
/** The default when a candidate has no setting: we hire the Title 24 consultant, the owner hires the rest
 *  (mirrors api/_consultantProposals.js#defaultHireBy). */
export function defaultHireBy(service) { return String(service || '').trim() === 'Energy Consultant' ? 'firm' : 'owner' }

export const REVIEW_STATUS_LABELS = {
  to_review: 'To review',
  sent_to_client: 'With the client',
  revision_asked: 'Revision asked for',
  declined: 'Declined',
  accepted: 'Accepted',
}

/** Save the facts step's answers on the PROJECT, so the next consultant's RFP starts filled in. */
export function saveRfpFacts(projectId, facts, senderName) {
  return call('POST', { action: 'saveFacts', projectId, facts, senderName }).then(d => d.facts)
}

function rfpBody(o) {
  return {
    projectId: o.projectId, projectConsultantId: o.projectConsultantId, orgAirtableId: o.orgAirtableId, orgName: o.orgName,
    consultantService: o.consultantService, recipients: o.recipients || [], dueDate: o.dueDate || null, notes: o.notes || null,
    facts: o.facts || {}, services: o.services || '', senderName: o.senderName, senderEmail: o.senderEmail,
  }
}

/** Exactly what Send would produce, stamped PREVIEW; nothing saved, nothing sent.
 *  → { pdfBase64, filename, email:{ subject, text } } */
export function previewConsultantRfp(o) {
  return call('POST', { action: 'previewRfp', ...rfpBody(o) })
}

/**
 * Generate the RFP PDF, save it, and (best-effort) email every recipient with it attached.
 * recipients: [{ contactAirtableId, name, email }]
 */
export function sendConsultantRfp(o) {
  return call('POST', { action: 'createAndSendRfp', ...rfpBody(o) }).then(d => d.rfp)
}

export function addConsultantProposal({ rfpId, projectConsultantId, proposedFee, dateReceived, notes, fileUrl }) {
  return call('POST', {
    action: 'createProposal', rfpId: rfpId || null, projectConsultantId,
    proposedFee: proposedFee != null && proposedFee !== '' ? Number(proposedFee) : null,
    dateReceived: dateReceived || null, notes: notes || null, fileUrl: fileUrl || null,
  }).then(d => d.proposal)
}

export function updateConsultantProposal(proposalId, fields) {
  return call('POST', { action: 'updateProposal', proposalId, fields }).then(d => d.proposal)
}

/** decision: 'approved' | 'revision_requested' | 'declined' */
export function recordProposalClientDecision(proposalId, decision, note, decidedAt) {
  return call('POST', { action: 'recordClientDecision', proposalId, decision, note: note || null, decidedAt: decidedAt || null }).then(d => d.proposal)
}

export function deleteConsultantRfp(rfpId) {
  return call('POST', { action: 'deleteRfp', rfpId }).then(d => d.ok)
}

export function deleteConsultantProposal(proposalId) {
  return call('POST', { action: 'deleteProposal', proposalId }).then(d => d.ok)
}

/**
 * Get a signed upload URL for a proposal file. Returns { signedUrl, publicUrl }.
 * Client PUTs the file to signedUrl, then passes publicUrl as fileUrl to addConsultantProposal.
 */
export async function getConsultantProposalUploadUrl(filename) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ action: 'consultantProposalUploadUrl', filename }),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || ('Could not get upload URL (' + res.status + ')'))
  }
  return res.json()
}

export async function uploadConsultantProposalFile(file) {
  const { signedUrl, publicUrl } = await getConsultantProposalUploadUrl(file.name)
  const uploadRes = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' },
    body: file,
  })
  if (!uploadRes.ok) {
    const detail = await uploadRes.text().catch(() => '')
    throw new Error('Storage upload failed (' + uploadRes.status + ')' + (detail ? ': ' + detail.slice(0, 200) : ''))
  }
  return publicUrl
}

/* ── Derived candidate lifecycle stage (pure — no fetch) ─────────────────────
 * Candidate = a Project Consultants row (services/projectConsultants.js) with Status='Candidate'.
 * rfps/proposals are the full project-wide arrays from getProjectConsultantRfpData, already
 * filtered/sorted client-side by caller — this just reads the newest of each. */

export function candidateRfps(projectConsultantId, rfps) {
  return (rfps || []).filter(r => r.project_consultant_airtable_id === projectConsultantId)
    .sort((a, b) => new Date(b.sent_at || 0) - new Date(a.sent_at || 0))
}
export function candidateProposals(projectConsultantId, proposals) {
  return (proposals || []).filter(p => p.project_consultant_airtable_id === projectConsultantId)
    .sort((a, b) => new Date(b.date_received || b.created_at || 0) - new Date(a.date_received || a.created_at || 0))
}
export function rfpProposals(rfpId, proposals) {
  return (proposals || []).filter(p => p.rfp_id === rfpId)
    .sort((a, b) => new Date(b.date_received || b.created_at || 0) - new Date(a.date_received || a.created_at || 0))
}

function fmtFee(fee) {
  if (fee == null) return ''
  return '$' + Number(fee).toLocaleString('en-US', { maximumFractionDigits: 0 })
}

/**
 * Derive a small { icon, label } stage badge for a candidate from its RFPs/proposals — never
 * stored, always recomputed so nothing can drift out of sync with the underlying records.
 */
export function deriveCandidateStage(projectConsultantId, rfps, proposals) {
  const myRfps = candidateRfps(projectConsultantId, rfps)
  const myProposals = candidateProposals(projectConsultantId, proposals)
  if (!myRfps.length && !myProposals.length) return { icon: '⏳', label: 'Candidate' }
  const latest = myProposals[0]
  if (!latest) {
    const sentDate = myRfps[0].sent_at ? String(myRfps[0].sent_at).slice(0, 10) : ''
    return { icon: '📨', label: 'RFP sent' + (sentDate ? ' ' + sentDate : '') }
  }
  if (latest.signed_at) return { icon: '✓', label: 'Signed' }
  if (latest.review_status === 'accepted') return { icon: '✓', label: 'Accepted' }
  if (latest.review_status === 'to_review') return { icon: '📥', label: (fmtFee(latest.proposed_fee) || 'Proposal') + ' · to review' }
  if (latest.review_status === 'revision_asked') return { icon: '🔁', label: 'Revision asked for' }
  if (latest.review_status === 'declined') return { icon: '✕', label: 'Declined' }
  if (latest.review_status === 'sent_to_client' && latest.client_decision === 'approved') return { icon: '✍', label: 'Client approved · signed copy to come' }
  if (latest.client_decision === 'approved') return { icon: '✓', label: 'Client approved — ready to award' }
  if (latest.client_decision === 'revision_requested') return { icon: '🔁', label: 'Revision requested' }
  if (latest.client_decision === 'declined') return { icon: '✕', label: 'Client declined' }
  if (latest.review_status === 'sent_to_client') return { icon: '💰', label: (fmtFee(latest.proposed_fee) || 'Proposal') + ' · on the Client Hub' }
  return { icon: '💰', label: (fmtFee(latest.proposed_fee) || 'Proposal') + ' · awaiting client' }
}
